import { Box, Card, styled, SxProps, Typography } from '@mui/material';
import { memo, PropsWithChildren, ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export interface WrapperCardWithTitleProps extends PropsWithChildren {
  sx?: SxProps;
  contentSx?: SxProps;
  titleSx?: SxProps;
  elevation?: number;
  title: string;
  adornment?: ReactNode;
}

/**
 *  Title on top-left of the card, content beneath, centered
 *  @param children - The content of the card
 *  @param title - The title of the card
 */
export const WrapperCardWithTitle = memo(function (props: WrapperCardWithTitleProps): ReactElement {
  const { t } = useTranslation();
  const { children, title, elevation, sx, contentSx, titleSx, adornment, ...rest } = props;

  return (
    <StyledCard elevation={elevation ?? 8} sx={sx} {...rest}>
      <Box
        sx={{
          paddingTop: 1,
          paddingBottom: 1,
          paddingRight: 2,
          paddingLeft: 2,
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: 2,
          boxSizing: 'border-box',
        }}
      >
        <Typography variant='h6' sx={titleSx}>
          {t(title)}
        </Typography>
        {adornment && adornment}
      </Box>
      <ContentWrapper sx={contentSx}>{children}</ContentWrapper>
    </StyledCard>
  );
});

const StyledCard = styled(Card)({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  gap: 2,
  height: '100%',
  width: '100%',
  borderRadius: '10px',
  overflow: 'hidden',
});

const ContentWrapper = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'auto',
  minHeight: 0, // Important for flex child to shrink
});
